import { useEffect } from 'react';
import { brand } from '../data/site';
const BRAND_BLUE = '#0511F2';
function upsertIcon(rel: string, href: string, sizes?: string) {
  const selector = sizes ? `link[rel="${rel}"][sizes="${sizes}"]` : `link[rel="${rel}"]:not([sizes])`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (sizes) link.setAttribute('sizes', sizes);
    document.head.appendChild(link);
  }
  link.type = href.startsWith('data:image/png') ? 'image/png' : '';
  link.href = href;
}

/** Renders the supplied Morvix mark onto a brand-blue tile at the given size. */
function renderTile(image: HTMLImageElement, size: number): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = BRAND_BLUE;
  ctx.fillRect(0, 0, size, size);

  // Contain the mark inside the tile with even padding, aspect ratio preserved.
  const pad = size * 0.16;
  const box = size - pad * 2;
  const ratio = image.width / image.height || 1;
  const width = ratio >= 1 ? box : box * ratio;
  const height = ratio >= 1 ? box / ratio : box;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Sets the browser-tab icon to the official Morvix Graphics mark, composited
 * onto a brand-blue tile so the white mark stays visible on light and dark tabs.
 * Generates 16/32/180px variants; falls back to the raw logo if the canvas
 * cannot be read (cross-origin).
 */
export function Favicon() {
  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    const applyFallback = () => {
      upsertIcon('icon', brand.logo);
      upsertIcon('apple-touch-icon', brand.logo);
    };
    image.onload = () => {
      if (cancelled) return;
      const small = renderTile(image, 32);
      const medium = renderTile(image, 64);
      const large = renderTile(image, 180);
      if (!small || !large) {
        applyFallback();
        return;
      }
      upsertIcon('icon', small, '16x16');
      upsertIcon('icon', small, '32x32');
      if (medium) upsertIcon('icon', medium, '64x64');
      upsertIcon('icon', large, '192x192');
      upsertIcon('apple-touch-icon', large);
      upsertIcon('shortcut icon', small);
    };
    image.onerror = () => {
      if (!cancelled) applyFallback();
    };
    image.src = brand.logo;
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}